import React, { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";

type ModalStyles = {
  content?: CSSProperties;
  overlay?: CSSProperties;
};

interface ModalProps {
  isOpen: boolean;
  onRequestClose?: () => void;
  children: ReactNode;
  className?: string;
  contentLabel?: string;
  style?: ModalStyles;
  aria?: {
    labelledby?: string;
  };
}

// Simple modal replacement for react-modal with minimal styling/ARIA support.
const Modal = ({
  isOpen,
  onRequestClose,
  children,
  className,
  contentLabel,
  style,
  aria,
}: ModalProps) => {
  if (!isOpen) return null;

  const contentStyles: CSSProperties = {
    position: "relative",
    background: "white",
    padding: 0,
    border: "none",
    borderRadius: 0,
    maxWidth: "90vw",
    maxHeight: "90vh",
    overflow: "auto",
    ...style?.content,
  };

  const overlayStyles: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...style?.overlay,
  };

  const content = (
    <div
      style={overlayStyles}
      onClick={event => {
        if (event.target === event.currentTarget && onRequestClose) {
          onRequestClose();
        }
      }}
    >
      <div
        className={className}
        style={contentStyles}
        role="dialog"
        aria-modal="true"
        aria-labelledby={aria?.labelledby}
        aria-label={contentLabel}
      >
        {children}
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(content, document.body)
    : content;
};

export default Modal;
